import React, { useState } from 'react'
import appBridge from 'libs/appBridge'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { useTranslation, Trans } from 'react-i18next'
import { Grid, Popup, Input, Icons } from '@shopline/dashboard-ui'
import { Button, Loading, EmptyString, NoData } from 'components'
import useLoading from 'hooks/useLoading'
import { apiUpdateVariationImages } from 'api/products'
import { getName, transferThumbnailURL, getImageFileExtension } from 'utils'
import styles from 'scss/VariationPopup.module.scss'

const StyledTipsBlock = styled.div`
  padding: 0 8px;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;

  p {
    color: #7a8499;
    margin: 0;
  }

  a {
    display: flex;
    align-items: center;
    color: #5087e6;

    svg {
      fill: #5087e6;
    }
  }
`

const StyledContainer = styled.div`
  max-width: 600px;
  margin: -8px;
`

const StyledImageContainer = styled.div`
  padding: 0 8px;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-auto-flow: row;
  gap: 24px;

  @media (max-width: 576px) {
    grid-template-columns: repeat(2, 1fr);
  }
`

const StyledImageCard = styled.div`
  height: 175px;
  border: solid 1px #cccccc;
  cursor: pointer;
  position: relative;
  border-radius: 8px;
  border: ${({ selected }) => (selected ? 'solid 2.5px #4287E2' : 'solid 1px #CED9E1')};
  overflow: hidden;
  box-sizing: border-box;

  p {
    padding: 5px;
    line-height: 1.25;
    border-top: solid 1px #ced9e1;
    width: 100%;
    height: 41px;
    overflow: hidden;
    font-size: 14px;
    white-space: normal;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    text-overflow: ellipsis;
    -webkit-box-orient: vertical;
    margin: 0;
    box-sizing: border-box;
  }

  &:after {
    content: '${({ order }) => order ?? ''}';
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 12px;
    position: absolute;
    color: #fff;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    top: 8px;
    right: 12px;
    background-color: ${({ selected }) => (selected ? '#4287E2' : 'unset')};
  }
`

const StyledImage = styled.img`
  width: 100%;
  height: 120px;
  object-fit: cover;
`

const StyledButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`

const StyledNodataSection = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`

const VariationEditPopup = ({ closePopup }) => {
  const { currentUrl } = appBridge.client
  const history = useHistory()
  const [photoName, setPhotoName] = useState('')
  const [filteredKeyword, setFilteredKeyword] = useState('')
  const [isLoading, openLoading, closeLoading] = useLoading()
  const { t, i18n } = useTranslation()

  const getImageAlt = (imgAlt) => {
    if (!imgAlt) return ''
    if (!imgAlt[i18n.language]) {
      const lang = Object.keys(i18n.options.resources).find(
        (lang) => lang.toLocaleLowerCase() !== i18n.language.toLocaleLowerCase(),
      )
      return imgAlt[lang] || ''
    } else {
      return imgAlt[i18n.language]
    }
  }
  const handleImageSearch = () => {
    setFilteredKeyword(photoName)
  }

  // redux
  const { variationImages, selectedVariation, selectedProduct } = useSelector((reduxState) => reduxState.products)
  const {
    user: { merchantId },
  } = useSelector((reduxState) => reduxState.user)
  const [selectedImages, setSelectedImages] = useState(variationImages.filter((imgObj) => imgObj.selected === true))

  // util

  const refreshPage = () => {
    history.go(0)
  }
  // handle state
  const handleToggleSelectedImage = (imgObj) => {
    const { id } = imgObj
    let updatedVariationImages

    const isImgObjSelected = selectedImages.filter((selectedImgObj) => selectedImgObj.id === id).length > 0

    if (isImgObjSelected) {
      updatedVariationImages = selectedImages.filter((selectedImgObj) => selectedImgObj.id !== id)
    } else {
      const updatedImgObj = { ...imgObj, selected: true }
      updatedVariationImages = [...selectedImages, updatedImgObj]
    }
    setSelectedImages(updatedVariationImages)
  }

  const getSelectedImgOrder = (imgObj) => {
    const { id } = imgObj
    const imageIndex = selectedImages.findIndex((selectedImgObj) => selectedImgObj.id === id)
    if (imageIndex === -1) return ''
    const imageOrder = imageIndex + 1
    return imageOrder
  }

  const getImgSelectedStatus = (imgObj) => {
    const { id } = imgObj
    const imageObj = selectedImages.find((selectedImgObj) => selectedImgObj.id === id)
    if (imageObj) return true
    return false
  }

  // api

  const handleApiUpdateVariationImages = async () => {
    openLoading()
    const productId = selectedProduct._id
    const variationId = selectedVariation._id
    const selectedImagesRequestData = selectedImages.map((imgObj) => ({ _id: imgObj.id, url: imgObj.url }))
    const bodyData = { selected_images: selectedImagesRequestData }
    await apiUpdateVariationImages({ productId, variationId, bodyData })
    closeLoading()
    refreshPage()
  }

  const showVariationImages = variationImages.filter((imgObj) => getImageAlt(imgObj.alt).includes(filteredKeyword))

  return (
    <div>
      {isLoading ? <Loading /> : null}
      <Popup
        title={t('variationPopupTitle')}
        onClose={closePopup}
        renderFooter={() => (
          <StyledButtonGroup>
            <Button color="primary" outline onClick={closePopup}>
              {t('cancel')}
            </Button>
            <Button color="primary" onClick={handleApiUpdateVariationImages}>
              {t('selectedImages', { imageNumber: selectedImages.length })}
            </Button>
          </StyledButtonGroup>
        )}
      >
        <StyledContainer>
          <Grid.Row>
            <Grid.Column lg={12}>
              <div className={styles['variation-breadcrumb']}>
                <div> {getName({ obj: selectedVariation.fields_translations, i18n: i18n.language })}</div>
              </div>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column md={10} lg={10} sd={9} sm={9}>
              <Input
                block
                status="valid"
                value={photoName}
                type="searchProduct"
                placeholder={t('photoSearchPlace')}
                onChange={(e) => setPhotoName(e.target.value)}
              />
            </Grid.Column>
            <Grid.Column md={2} lg={2} sd={3} sm={3}>
              <Button color="primary" onClick={handleImageSearch} block>
                {t('search')}
              </Button>
            </Grid.Column>
          </Grid.Row>

          <StyledTipsBlock>
            <p>{t('selectPhotoOrder')}</p>
            <a
              href={`${currentUrl.slice(0, currentUrl.indexOf('/apps'))}/products/${selectedProduct._id}/edit`}
              target="_blank"
            >
              {t('editProduct')} <Icons.Outline.Link size="MEDIUM" />
            </a>
          </StyledTipsBlock>
          {showVariationImages.length > 0 ? (
            <StyledImageContainer>
              {showVariationImages.map((imgObj) => (
                <StyledImageCard
                  title={getImageAlt(imgObj.alt)}
                  key={imgObj.id}
                  selected={getImgSelectedStatus(imgObj)}
                  order={getSelectedImgOrder(imgObj)}
                  onClick={() => handleToggleSelectedImage(imgObj)}
                >
                  <StyledImage
                    src={transferThumbnailURL(imgObj.id, merchantId, getImageFileExtension(imgObj.url))}
                    alt={getImageAlt(imgObj.alt)}
                  />
                  <p className={styles['variation-photo-name']}>{getImageAlt(imgObj.alt) || <EmptyString />}</p>
                </StyledImageCard>
              ))}
            </StyledImageContainer>
          ) : (
            <StyledNodataSection>
              <NoData
                tips={
                  <Trans
                    i18nKey="tryAnotherKeyword"
                    components={{
                      a: (
                        <a
                          href={`${currentUrl.slice(0, currentUrl.indexOf('/apps'))}/products/${
                            selectedProduct._id
                          }/edit`}
                          target="_blank"
                        />
                      ),
                    }}
                  />
                }
              />
            </StyledNodataSection>
          )}
        </StyledContainer>
      </Popup>
    </div>
  )
}

export default VariationEditPopup
